import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { extractApiErrorMessage } from '@/lib/api';
import { getInvitation, postAcceptInvitation } from '@/lib/auth';
import { useAuthStore } from '@/stores/auth-store';

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { error, success } = useToast();
  const setSession = useAuthStore((s) => s.setSession);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const invitationQuery = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => getInvitation(token ?? ''),
    enabled: Boolean(token),
    retry: false,
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token || submitting) return;
    if (password.length < 10) {
      error('Password too short', 'Minimum 10 characters');
      return;
    }
    if (password !== confirm) {
      error('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      const session = await postAcceptInvitation(token, name, password);
      setSession(session);
      success(
        'Welcome aboard',
        `You joined ${session.user.memberships[0]?.organizationName ?? 'the organization'}`,
      );
      navigate('/', { replace: true });
    } catch (err) {
      error('Could not accept invitation', extractApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const expiresAt = invitationQuery.data ? new Date(invitationQuery.data.expiresAt) : null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl">Accept invitation</CardTitle>
          <CardDescription>
            {invitationQuery.isLoading ? (
              'Verifying invitation…'
            ) : invitationQuery.isError ? (
              'This invitation link is no longer valid.'
            ) : invitationQuery.data ? (
              <>
                Join <strong>{invitationQuery.data.organizationName}</strong> as{' '}
                <em>{invitationQuery.data.roleName}</em>. Invitation addressed to{' '}
                <strong>{invitationQuery.data.inviteeEmail}</strong>.
              </>
            ) : (
              'Missing invitation token.'
            )}
          </CardDescription>
          {expiresAt ? (
            <p className="text-xs text-muted-foreground">Expires {expiresAt.toLocaleString()}</p>
          ) : null}
        </CardHeader>
        <CardContent>
          {invitationQuery.isError ? (
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>
                {extractApiErrorMessage(
                  invitationQuery.error,
                  'The invitation may have expired or been revoked.',
                )}
              </p>
              <Button className="w-full" onClick={() => navigate('/login')}>
                Go to sign in
              </Button>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit} noValidate>
              <div className="space-y-2">
                <Label htmlFor="invite-name">Your name</Label>
                <Input
                  id="invite-name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jordan Lee"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-password">Choose a password</Label>
                <Input
                  id="invite-password"
                  type="password"
                  required
                  minLength={10}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-confirm">Confirm password</Label>
                <Input
                  id="invite-confirm"
                  type="password"
                  required
                  minLength={10}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  At least 10 characters, one number, and one symbol.
                </p>
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={submitting || !invitationQuery.data}
              >
                {submitting ? 'Joining…' : 'Accept invitation'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
